import { HttpStatusCode } from '@angular/common/http';
import { inject, Injector, Service } from '@angular/core';
import type {
  OnyxDropdownOptionsSourceResult,
  OnyxFilter,
  OnyxHttpErrorResponse,
  OnyxOption,
  OnyxPaginated,
  OnyxPagination,
} from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { isString, omit } from 'lodash';
import type { Observable } from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  from,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import type { PickDeep } from 'type-fest';
import type { RoutePointStatusFormDto } from '../../../../common/components/route/route-point/route-point-status-form/route-point-status-form.component';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { TelematicsRoutePointFactory } from '../../../../common/factories/telematics/telematics-route-point.factory';
import { ConflictHelper } from '../../../../common/helpers/conflict.helper';
import { FilterHelper } from '../../../../common/helpers/filter.helper';
import { RouteHelper } from '../../../../common/helpers/route.helper';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type {
  TelematicsRoutePoint,
  TelematicsRoutePointDto,
} from '../../../../common/interfaces/telematics/telematics-route-point';
import type { BatchFileUpload } from '../../../../common/interfaces/utilities/batch-file-upload';
import { FleetIdentifierPipe } from '../../../../common/pipes/fleet-identifier.pipe';
import { ApiService } from '../../../../common/services/api.service';
import { StorageService } from '../../../../common/services/storage.service';
import { TelematicsDataService } from '../../../../common/services/telematics-data.service';
import type { ImportProgress } from '../../../common/interfaces/import-progress';
import { DriversService } from '../../../drivers/common/services/drivers.service';
import type { CompanyVehicleParameter } from '../../../management-panel/dictionaries/common/interfaces/company-vehicle-parameter';
import { EmployeesService } from '../../../management-panel/employees/common/services/employees.service';
import type { Swap, SwapDto } from '../../../map/common/interfaces/swap/swap';
import type { ServiceFormDto } from '../../../map/map/map-side-panel/map-side-panel-service/map-side-panel-service-details/map-side-panel-service-details.component';
import type { MapSwapFormDto } from '../../../map/map/map-swap-panel/map-swap-panel.component';
import type { FleetAssignedDriversModalForm } from '../components/fleet-assigned-drivers-modal/fleet-assigned-drivers-modal.component';
import type { FleetImportData } from '../constants/fleet-import-schema';
import type { FleetCategory } from '../enums/fleet-category';
import type { FleetSort } from '../enums/fleet-sort';
import type { FleetState } from '../enums/fleet-state';
import type { FleetFormDto } from '../helpers/fleet-form.helper';
import type { Fleet } from '../interfaces/fleet';

export interface FleetFilters {
  filters?: OnyxFilter[];
  query?: string;
  sort?: FleetSort;
}

@Service()
export class FleetService extends ApiService {
  private readonly storageService = inject(StorageService);
  private readonly fleetIdentifierPipe = inject(FleetIdentifierPipe);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly conflictHelper = inject(ConflictHelper);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listFleet(
    params: OnyxPagination & FleetFilters,
  ): Observable<OnyxPaginated<Fleet>> {
    return this.get('/fleet', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.filters && FilterHelper.buildQueryParams(params.filters)),
        ...(params.query && { query: params.query }),
        ...(params.sort && { 'sort[]': params.sort }),
      },
    });
  }

  public searchFleet(
    query: string,
    params: Omit<OnyxPagination & FleetFilters, 'query'>,
    options?: {
      showSet?: boolean;
      showAssignedTrailer?: boolean;
      showVinNumber?: boolean;
    },
  ): Observable<OnyxDropdownOptionsSourceResult<Fleet>> {
    return this.reload$.pipe(
      startWith(undefined),
      switchMap(() => this.listFleet({ ...params, query })),
      map((response) => ({
        options: response.items.map((fleet): OnyxOption<Fleet> => ({
          name: options?.showSet
            ? `${this.fleetIdentifierPipe.transform(fleet, 'bracket')} · ${this.fleetIdentifierPipe.transform(
                fleet.trailer,
                'bracket',
              )}`
            : `${this.fleetIdentifierPipe.transform(fleet)}${
                options?.showAssignedTrailer
                  ? fleet.trailer
                    ? ` / ${this.fleetIdentifierPipe.transform(fleet.trailer)}`
                    : ''
                  : ''
              }`,
          value: fleet,
          leftIcon: {
            name: `${fleet.generalInformation.category}-outlined`,
            size: 16,
          },
          leftIconColor: 'gray-alt' as const,
          ...(options?.showVinNumber && {
            description: fleet.generalInformation.vinNumber,
          }),
        })),
        totalItems: response.totalItems,
      })),
    );
  }

  public getFleet(uuid: string): Observable<Fleet> {
    return this.get<Fleet>(`/fleet/${uuid}`).pipe(
      catchError((response) => this.handleNotFoundError(response)),
    );
  }

  public addFleet(dto: FleetFormDto): Observable<Fleet> {
    return this.uploadFiles(dto).pipe(
      switchMap((dto) => this.post<Fleet>(`/${this.getPath(dto)}`, dto)),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public importFleet(data: FleetImportData[]): Observable<ImportProgress> {
    const dto = data.map(
      (item): FleetFormDto =>
        ({
          ...omit(item, 'index', 'base'),
          additionalParameters: {
            ...item.additionalParameters,
            otherVehicleParameters:
              item.additionalParameters.otherVehicleParameters?.map(
                (parameter: CompanyVehicleParameter) => parameter.uuid,
              ) ?? null,
          },
        }) as FleetFormDto,
    );

    return from(dto).pipe(
      concatMap((dto, index) =>
        this.addFleet(dto).pipe(
          map(() => ({ ok: true, index })),
          defaultIfEmpty({ ok: false, index }),
          catchError((response) => {
            ValidationHelper.handleUnexpectedResponse(response, this.injector);
            return of({ ok: false, index });
          }),
        ),
      ),
    );
  }

  public editFleet(uuid: string, dto: FleetFormDto): Observable<Fleet> {
    return this.uploadFiles(dto).pipe(
      switchMap((dto) => this.put<Fleet>(`/${this.getPath(dto)}/${uuid}`, dto)),
      catchError((response) => this.handleConflictErrors(dto, response)),
      tap(() => this._reload$.next()),
    );
  }

  public deleteFleet(dto: Fleet): Observable<void> {
    return this.delete<void>(`/${this.getPath(dto)}/${dto.uuid}`).pipe(
      tap(() => this._reload$.next()),
    );
  }

  public getSwap(uuid: string): Observable<Swap> {
    return this.get<SwapDto>(`/fleet/swaps/${uuid}`).pipe(
      map((dto) => this.mapSwapFromDto(dto)),
    );
  }

  public addSwap(dto: MapSwapFormDto): Observable<void> {
    return this.post('/fleet/swaps', dto);
  }

  public deleteSwap(uuid: string): Observable<void> {
    return this.delete(`/fleet/swaps/${uuid}`);
  }

  public editTechnicalPointStatus(
    vehicleUuid: string,
    pointUuid: string,
    dto: RoutePointStatusFormDto,
  ): Observable<void> {
    return this.put<void>(
      `/vehicles/${vehicleUuid}/technical-points/${pointUuid}/status`,
      dto,
    ).pipe(
      tap(() =>
        this.updateTelematicsTechnicalPointStatus(vehicleUuid, pointUuid, dto),
      ),
      tap(() => this._reload$.next()),
      catchError((response) =>
        RouteHelper.handlePointStatusError(response, this.toastService),
      ),
    );
  }

  public getAssignedRoute(uuid: string): Observable<TelematicsRoutePoint[]> {
    return this.get<TelematicsRoutePointDto[]>(
      `/fleet/${uuid}/assigned-route`,
    ).pipe(
      map((routePoints) => TelematicsRoutePointFactory.fromDto(routePoints)),
      catchError((response) => this.handleNotFoundError(response)),
    );
  }

  public updateAssignedDrivers(
    uuid: string,
    form: FleetAssignedDriversModalForm,
  ): Observable<void> {
    return this.put<void>(`/vehicles/${uuid}/assigned-drivers`, form).pipe(
      tap(() => this.reloadDrivers()),
    );
  }

  public unassignDriver(
    vehicleUuid: string,
    driverUuid: string,
  ): Observable<void> {
    return this.delete<void>(
      `/vehicles/${vehicleUuid}/assigned-drivers/${driverUuid}`,
    ).pipe(tap(() => this.reloadDrivers()));
  }

  public updateAssignedEmployees(
    uuid: string,
    employeesUuid: string[],
  ): Observable<void> {
    return this.put<void>(`/vehicles/${uuid}/assigned-employees`, {
      employees: employeesUuid,
    }).pipe(tap(() => this.reloadEmployees()));
  }

  public batchFleet(
    fleetUuid: string[],
    setFields?: {
      isForSale?: boolean;
      state?: FleetState;
      assignedEmployees?: string[];
    },
    actions: string[] = [],
  ): Observable<void> {
    return this.put<void>('/fleet/batch', {
      fleetUuid,
      setFields,
      actions,
    }).pipe(
      tap(() =>
        setFields?.assignedEmployees
          ? this.reloadEmployees()
          : this._reload$.next(),
      ),
    );
  }

  public assignTrailer(
    vehicleUuid: string,
    trailerUuid: string | null,
    reload = true,
  ): Observable<void> {
    return this.put<void>(`/vehicles/${vehicleUuid}/trailer`, {
      trailerUuid,
    }).pipe(
      tap(() => {
        if (reload) this._reload$.next();
      }),
      catchError((response) => this.handleAssignTrailerError(response)),
    );
  }

  public addService(
    vehicleUuid: string,
    dto: ServiceFormDto,
  ): Observable<void> {
    return this.post(`/service/${vehicleUuid}`, dto);
  }

  private getPath(
    dto: FleetCategory | PickDeep<Fleet, 'generalInformation.category'>,
  ): string {
    const category = isString(dto) ? dto : dto.generalInformation.category;
    return category.includes('trailer') ? 'trailers' : 'vehicles';
  }

  private uploadFiles(dto: FleetFormDto): Observable<FleetFormDto> {
    return of(dto).pipe(
      map((dto): BatchFileUpload[] => [
        {
          path: 'registrationCertificate.scan',
          files: dto.registrationCertificate.scan,
        },
        ...(dto.co2Emissions
          ? [
              {
                path: 'co2Emissions.emissionCertificate',
                files: dto.co2Emissions.emissionCertificate,
              },
            ]
          : []),
        {
          path: 'thirdPartyLiabilityInsurance.contractScan',
          files: dto.thirdPartyLiabilityInsurance.contractScan,
        },
        ...(dto.comprehensiveInsurance
          ? [
              {
                path: 'comprehensiveInsurance.contractScan',
                files: dto.comprehensiveInsurance.contractScan,
              },
            ]
          : []),
      ]),
      switchMap((data) => this.storageService.uploadBatch(data)),
      map((data) => this.storageService.mergeBatch(dto, data)),
    );
  }

  private handleAssignTrailerError(
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const ERRORS: [string, string][] = [
      [
        'Trailer with given UUID does not exist',
        'fleet.toasts.trailerNotFound',
      ],
      [
        'Trailer with given UUID can not be assigned',
        'fleet.toasts.trailerCannotBeAssigned',
      ],
      [
        'semi-trailer can be assigned only to a semi-truck',
        'fleet.toasts.semiTrailerOnlyForSemiTruck',
      ],
      [
        'Trailer with size type mega can only be assigned to truck with low deck',
        'fleet.toasts.megaTrailerRequiresLowDeck',
      ],
      [
        'Object has other delegations in provided data range',
        'delegations.delegationsForm.trailerHasDelegation',
      ],
    ];

    const error = JSON.stringify(response.error ?? '');

    for (const [match, message] of ERRORS) {
      if (!error.includes(match)) continue;

      this.toastService.showError({ message });
      response.handled = true;
      break;
    }

    throw response;
  }

  private handleNotFoundError(
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (response.status === HttpStatusCode.NotFound) {
      this.toastService.showError({
        message: 'fleet.toasts.vehicleNotFound',
        keepOnNavigation: true,
      });
      response.handled = true;
    }

    throw response;
  }

  private handleConflictErrors(
    dto: FleetFormDto,
    response: OnyxHttpErrorResponse,
  ): Observable<never> {
    if (!VALIDATION_ERROR_CODES.includes(response.status)) throw response;

    const I18N = 'fleet.fleetForm';
    const CONFLICT_FIELDS = [
      {
        key: 'generalInformation.registrationNumber',
        message: `${I18N}.uniqueErrors.registrationNumber`,
        params: {
          registrationNumber: dto.generalInformation?.registrationNumber,
        },
      },
      {
        key: 'generalInformation.sideNumber',
        message: `${I18N}.uniqueErrors.sideNumber`,
        params: {
          sideNumber: dto.generalInformation.sideNumber,
        },
      },
      {
        key: 'generalInformation.vinNumber',
        message: `${I18N}.uniqueErrors.vinNumber`,
        params: {
          vinNumber: dto.generalInformation.vinNumber,
        },
      },
      {
        key: 'licensesAndPermits.communityLicenseAbstractNumber',
        message: `${I18N}.uniqueErrors.communityLicenseAbstractNumber`,
        params: {
          communityLicenseAbstractNumber:
            dto.licensesAndPermits?.communityLicenseAbstractNumber,
        },
      },
      {
        key: 'licensesAndPermits.permitAbstractNumber',
        message: `${I18N}.uniqueErrors.permitAbstractNumber`,
        params: {
          permitAbstractNumber: dto.licensesAndPermits?.permitAbstractNumber,
        },
      },
      {
        key: 'thirdPartyLiabilityInsurance.insuranceNumber',
        message: `${I18N}.uniqueErrors.thirdPartyLiabilityInsuranceNumber`,
        params: {
          thirdPartyLiabilityInsuranceNumber:
            dto.thirdPartyLiabilityInsurance?.insuranceNumber,
        },
      },
      {
        key: 'comprehensiveInsurance.insuranceNumber',
        message: `${I18N}.uniqueErrors.comprehensiveInsuranceNumber`,
        params: {
          comprehensiveInsuranceNumber:
            dto.comprehensiveInsurance?.insuranceNumber,
        },
      },
    ];

    this.conflictHelper.handleConflictErrors(response, CONFLICT_FIELDS);

    throw response;
  }

  private updateTelematicsTechnicalPointStatus(
    fleetUuid: string,
    pointUuid: string,
    dto: RoutePointStatusFormDto,
  ): void {
    const telematicsService = this.injector.get(TelematicsDataService);
    const { arrivedAt, startedAt, completedAt, status } = dto;

    telematicsService.updateTechnicalPointStatus({
      fleetUuid,
      technicalPointUuid: pointUuid,
      status,
      timestamps: {
        arrivedAt,
        startedAt,
        completedAt,
        canceledAt: null,
      },
    });
  }

  private mapSwapFromDto(dto: SwapDto): Swap {
    const mapRoute = (
      routePoints: TelematicsRoutePointDto[],
    ): TelematicsRoutePoint[] =>
      routePoints.map((routePoint) => {
        const { point, technicalPoint, route } = routePoint;
        if (point) {
          return {
            ...routePoint,
            point: { ...point, route },
          } as TelematicsRoutePoint;
        }

        if (technicalPoint) {
          return {
            ...routePoint,
            technicalPoint: { ...technicalPoint, route },
          } as TelematicsRoutePoint;
        }

        return routePoint as TelematicsRoutePoint;
      });

    return {
      ...dto,
      vehicleRoute: mapRoute(dto.vehicleRoute),
      initialVehicleRoute: mapRoute(dto.initialVehicleRoute),
      otherVehicleRoute: dto.otherVehicleRoute
        ? mapRoute(dto.otherVehicleRoute)
        : null,
      initialOtherVehicleRoute: dto.initialOtherVehicleRoute
        ? mapRoute(dto.initialOtherVehicleRoute)
        : null,
      vehicleSemiTrailerPoints: mapRoute(dto.vehicleSemiTrailerPoints),
      vehicleUnassignedPoints: mapRoute(dto.vehicleUnassignedPoints),
      otherVehicleSemiTrailerPoints: dto.otherVehicleSemiTrailerPoints
        ? mapRoute(dto.otherVehicleSemiTrailerPoints)
        : null,
      otherVehicleUnassignedPoints: dto.otherVehicleUnassignedPoints
        ? mapRoute(dto.otherVehicleUnassignedPoints)
        : null,
    };
  }

  private reloadDrivers(): void {
    this._reload$.next();
    this.injector.get(DriversService)['_reload$'].next();
  }

  private reloadEmployees(): void {
    this._reload$.next();
    this.injector.get(EmployeesService)['_reload$'].next();
  }
}
